import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useApplyAccessRightsTemplateMutation,
  useDeleteAccessRightsTemplateMutation,
  useLazyGetAccessRightsTemplatesQuery,
  useUpdateAccessRightsTemplateMutation,
} from '@app/api/AccessRights';
import type { ModelAccessRightsTemplate } from '@app/api/AccessRights';
import { FRONT_WARNING, SUCCESS } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type {
  CreateTemplateFormInterface,
  UseAccessRightsTemplatesProps,
} from '@pages/Users/AccessRightsTab/types';

export const useAccessRightsTemplates = ({
  editableUserAccessRights,
  onChangeEditableUserUnsavedAccessRights,
  editableUserUnsavedAccessRights,
}: UseAccessRightsTemplatesProps) => {
  const [isTemplateCreationDialog, setTemplateCreationDialog] = useState(false);
  const [isTemplateApplyingDialog, setTemplateApplyingDialog] = useState(false);
  const [isTemplateDeletionDialog, setTemplateDeletionDialog] = useState(false);

  const [templates, setTemplates] = useState<ModelAccessRightsTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ModelAccessRightsTemplate | null>(null);
  const { activeBranchId, userId } = useUserPrint();
  const { showNotification } = useNotification();
  const { userAccessRights } = useNewUserAccessRights();
  const [searchParams] = useSearchParams();

  const [getAccessRightsTemplates, { isLoading: isTemplatesLoading }] =
    useLazyGetAccessRightsTemplatesQuery();

  const [
    deleteAccessRightsTemplate,
    { isLoading: isDeleteAccessRightsTemplateLoading },
  ] = useDeleteAccessRightsTemplateMutation();

  const [
    applyAccessRightsTemplate,
    { isLoading: isApplyAccessRightsTemplateLoading },
  ] = useApplyAccessRightsTemplateMutation();

  const [
    updateAccessRightsTemplate,
    { isLoading: isUpdateAccessRightsTemplateLoading },
  ] = useUpdateAccessRightsTemplateMutation();

  const editableUserId = useMemo(
    () => Number(searchParams.get('id')),
    [searchParams]
  );

  const showDeleteTemplateButton = useMemo(() => {
    const { AccessRights } = userAccessRights;

    return AccessRights.IsDeleteTemplates;
  }, [userAccessRights]);

  const showTemplates = useMemo(() => {
    if (!editableUserAccessRights) {
      return false;
    }
    const { IsSuperAdmin, IsAdmin } = editableUserAccessRights;

    return !(IsSuperAdmin || IsAdmin);
  }, [editableUserAccessRights]);

  const onSelectTemplate = useCallback(
    (template: ModelAccessRightsTemplate) => {
      setSelectedTemplate(template);
      setTemplateApplyingDialog(true);
    },
    []
  );

  const onDeleteTemplate = useCallback(
    (template: ModelAccessRightsTemplate) => {
      setSelectedTemplate(template);
      setTemplateDeletionDialog(true);
    },
    []
  );

  const openCreateTemplateDialog = useCallback(() => {
    setTemplateCreationDialog(true);
  }, []);

  const onCancelTemplateApplyingModal = useCallback(() => {
    setSelectedTemplate(null);
    setTemplateApplyingDialog(false);
  }, []);

  const onCancelTemplateDeletionModal = useCallback(() => {
    setSelectedTemplate(null);
    setTemplateDeletionDialog(false);
  }, []);

  const onCancelTemplateCreationModal = useCallback(() => {
    setTemplateCreationDialog(false);
  }, []);

  const getAccessRightsTemplatesHandler = useCallback(async () => {
    if (!activeBranchId) {
      return showNotification(
        'Не удалось получить идентификатор активного филиала',
        FRONT_WARNING
      );
    }
    try {
      const response = await getAccessRightsTemplates({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setTemplates(response);
    } catch (error) {
      showNotification(error);
    }
  }, [activeBranchId, getAccessRightsTemplates, showNotification]);

  const applyAccessRightsTemplateHandler = useCallback(async () => {
    if (!editableUserId) {
      return showNotification(
        'Не удалось получить идентификатор пользователя',
        FRONT_WARNING
      );
    }

    if (!activeBranchId) {
      return showNotification(
        'Не удалось получить идентификатор активного филиала',
        FRONT_WARNING
      );
    }

    if (!selectedTemplate) {
      return showNotification(
        'Не удалось получить выбранный шаблон прав доступа',
        FRONT_WARNING
      );
    }

    try {
      const response = await applyAccessRightsTemplate({
        ActiveBranchId: activeBranchId,
        AccessRightsTemplateId: selectedTemplate.Id,
        UserId: editableUserId,
      }).unwrap();

      onChangeEditableUserUnsavedAccessRights(response);
      onCancelTemplateApplyingModal();
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    editableUserId,
    selectedTemplate,
    applyAccessRightsTemplate,
    showNotification,
    onChangeEditableUserUnsavedAccessRights,
    onCancelTemplateApplyingModal,
  ]);

  const deleteAccessRightsTemplateHandler = useCallback(async () => {
    if (!selectedTemplate) {
      return showNotification(
        'Не удалось получить выбранный шаблон прав доступа',
        FRONT_WARNING
      );
    }

    if (!activeBranchId) {
      return showNotification(
        'Не удалось получить идентификатор активного филиала',
        FRONT_WARNING
      );
    }
    try {
      await deleteAccessRightsTemplate({
        ActiveBranchId: activeBranchId,
        templateId: selectedTemplate.Id,
      }).unwrap();

      setTemplates((prev) => {
        const updatedTemplates = prev?.filter(
          (template) => template.Id !== selectedTemplate.Id
        );
        return updatedTemplates;
      });

      showNotification('Шаблон прав удален', SUCCESS);
      onCancelTemplateDeletionModal();
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    selectedTemplate,
    deleteAccessRightsTemplate,
    showNotification,
    onCancelTemplateDeletionModal,
  ]);

  const createAccessRightsTemplateHandler = useCallback(
    async ({ Name, Description }: CreateTemplateFormInterface) => {
      if (!editableUserUnsavedAccessRights) {
        return showNotification(
          'Не удалось получить права доступа для редактируемого пользователя',
          FRONT_WARNING
        );
      }
      if (!editableUserId) {
        return showNotification(
          'Не удалось получить идентификатор пользователя',
          FRONT_WARNING
        );
      }

      if (!activeBranchId) {
        return showNotification(
          'Не удалось получить идентификатор активного филиала',
          FRONT_WARNING
        );
      }

      if (!userId) {
        return showNotification(
          'Не удалось получить идентификатор текущего пользователя',
          FRONT_WARNING
        );
      }

      if (!Name) {
        return showNotification('Введите название шаблона', FRONT_WARNING);
      }

      try {
        const response = await updateAccessRightsTemplate({
          ActiveBranchId: activeBranchId,
          data: {
            Id: 0,
            TimeCreated: moment().toISOString(),
            CreateUserId: userId,
            FromUserId: editableUserId,
            Name,
            Description: Description || '',
            ModelAccessRights: editableUserUnsavedAccessRights,
          },
        }).unwrap();

        setTemplates((prev) => [...prev, response]);
        onCancelTemplateCreationModal();
        showNotification('Шаблон успешно создан', SUCCESS);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      activeBranchId,
      editableUserId,
      userId,
      editableUserUnsavedAccessRights,
      updateAccessRightsTemplate,
      showNotification,
      onCancelTemplateCreationModal,
    ]
  );

  return {
    showTemplates,
    templates,
    showDeleteTemplateButton,
    selectedTemplate,

    onSelectTemplate,
    onDeleteTemplate,

    isTemplateCreationDialog,
    isTemplateApplyingDialog,
    isTemplateDeletionDialog,

    isTemplatesLoading,
    isApplyAccessRightsTemplateLoading,
    isDeleteAccessRightsTemplateLoading,
    isUpdateAccessRightsTemplateLoading,

    getAccessRightsTemplatesHandler,
    applyAccessRightsTemplateHandler,
    deleteAccessRightsTemplateHandler,
    createAccessRightsTemplateHandler,

    onCancelTemplateApplyingModal,
    onCancelTemplateDeletionModal,
    onCancelTemplateCreationModal,
    openCreateTemplateDialog,
  };
};
